import {
  Coupon_BrandAndCategory,
  Coupon_HighTierCoupon_ProductsInformation,
  Coupon_HotDealCoupon_ProductAndBrandInformation,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coupon.types';
import {Product} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product.types';
import {
  BadgeWithBorderRadius,
  BrandLogo,
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
} from '@aboutyou/ui-components';
import React, {FC, type JSX} from 'react';
import styled, {css} from 'styled-components';
import {DownloadAppButton} from './DownloadAppButton';
import {
  DownloadAppQr,
  DownloadText,
  QRCode,
  ScanQRText,
  paddingCssVariable,
} from './DownloadAppQr';
import {ProductBox} from './ProductBox';
import {ProductsInformation} from './ProductsInformation';
import {ANIMATION_DURATION_TIME, FOOTER_HEIGHT} from './constants';

interface FooterProps {
  brandAndCategory?: Coupon_BrandAndCategory;
  productAndBrandInformation?: Coupon_HotDealCoupon_ProductAndBrandInformation;
  className?: string;
  isOnlyAppCoupon: boolean;
  supportsHover?: boolean;
  ['data-testid']?: string;
  isDisabled?: boolean;
  isDisplayMode?: boolean;
  setSelectedProduct?: (product: Product | undefined) => void;
  activateCouponOnAddToBasket?: VoidFunction;
  productsInformation?: Coupon_HighTierCoupon_ProductsInformation;
}

const BRAND_LOGO_WIDTH = 44;

export const CouponFooter = ({
  brandAndCategory,
  productAndBrandInformation,
  className,
  isOnlyAppCoupon,
  supportsHover,
  'data-testid': dataTestId,
  isDisabled,
  isDisplayMode,
  setSelectedProduct,
  activateCouponOnAddToBasket,
  productsInformation,
  ref,
}: FooterProps & {ref?: React.Ref<HTMLDivElement>}) => {
  if (
    !brandAndCategory &&
    !productAndBrandInformation &&
    !productsInformation
  ) {
    return null;
  }

  const {brandLogo, brandName} = brandAndCategory?.brands.length
    ? {
        brandLogo: brandAndCategory.brands[0]?.image,
        brandName: brandAndCategory.brands[0]?.name,
      }
    : {
        brandLogo: productAndBrandInformation?.brand.blackLogoImage,
        brandName: productAndBrandInformation?.brand.name,
      };

  const brandLogoComponent = brandLogo ? (
    <StyledBrandLogo
      data-testid={`${dataTestId}BrandLogo`}
      name={brandName || ''}
      showTooltip={false}
      image={brandLogo}
      maxWidth={`${BRAND_LOGO_WIDTH}px`}
      maxHeight="36px"
      $isDisabled={isDisabled}
    />
  ) : null;

  if (productsInformation && !isOnlyAppCoupon) {
    return (
      <Wrapper ref={ref} className={className}>
        <ProductsInformation productsInformation={productsInformation} />
      </Wrapper>
    );
  }

  if (isDisplayMode && productAndBrandInformation) {
    return (
      <ProductBox
        price={productAndBrandInformation.price}
        product={productAndBrandInformation.product}
        brand={productAndBrandInformation.brand}
        isOnlyAppCoupon={isOnlyAppCoupon}
        isDisabled={isDisabled}
        supportsHover={supportsHover}
        setSelectedProduct={setSelectedProduct}
        activateCouponOnAddToBasket={activateCouponOnAddToBasket}
        priceV2={productAndBrandInformation.priceV2}
      />
    );
  }

  if (isOnlyAppCoupon && supportsHover) {
    return (
      <QRCodeFooter
        data-testid={dataTestId}
        className={className}
        isDisabled={isDisabled}
        brandLogoComponent={brandLogoComponent}
      />
    );
  }

  return (
    <Wrapper ref={ref} className={className} $justifyEnd={!brandLogo}>
      {brandLogoComponent}

      {!isOnlyAppCoupon && brandAndCategory?.categoryName && (
        <StyledBadgeWithBorderRadius
          text={brandAndCategory.categoryName}
          backgroundColor={COLORS.BackgroundCoupon}
          textColor={COLORS.PrimaryCoupon}
          dataTestId={`${dataTestId}CategoryName`}
          $isDisabled={isDisabled}
        />
      )}

      {isOnlyAppCoupon && (
        <DownloadAppButton
          data-testid={`${dataTestId}DownloadButton`}
          $isDisabled={isDisabled}
        />
      )}
    </Wrapper>
  );
};

const QRCodeFooter: FC<{
  ['data-testid']?: string;
  className?: string;
  isDisabled?: boolean;
  brandLogoComponent: JSX.Element | null;
}> = ({
  'data-testid': dataTestId,
  className,
  isDisabled,
  brandLogoComponent,
}) => {
  return (
    <HoverFooterWrapper
      className={className}
      data-testid={`${dataTestId}HoverFooter`}
    >
      {brandLogoComponent}

      <StyledDownloadAppQr isDisabled={isDisabled} />
    </HoverFooterWrapper>
  );
};

const QR_CODE_HEIGHT_WHEN_HOVER = 72;
const QR_CODE_LEFT_PADDING = 35;

const StyledBrandLogo = styled(BrandLogo)<{
  $isDisabled?: boolean;
}>`
  display: flex;
  align-items: center;
  width: fit-content;
  transition: transform ${ANIMATION_DURATION_TIME}ms ease-in-out,
    opacity ${ANIMATION_DURATION_TIME}ms ease-in-out;

  ${({$isDisabled}) =>
    $isDisabled &&
    css`
      filter: opacity(0.5) grayscale(1);
    `}
`;

const HoverFooterWrapper = styled.div`
  --hover-footer-padding: 16px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  width: 100%;
  z-index: 1;
  height: ${FOOTER_HEIGHT}px;
  max-height: ${FOOTER_HEIGHT}px;
  background-color: ${COLORS.White};
  display: flex;
  padding-inline: var(--hover-footer-padding);

  // eslint-disable-next-line local-rules/css-layout-animation
  transition-property: height, max-height, border-radius, border-color;
  transition-duration: ${ANIMATION_DURATION_TIME}ms;
  transition-timing-function: ease-in-out;

  &:hover {
    border-radius: 16px;
    // plus 1px to hide the border top of the footer after hover
    height: calc(100% + 1px);
    max-height: calc(100% + 1px);

    ${QRCode} {
      height: ${QR_CODE_HEIGHT_WHEN_HOVER}px;
      right: calc(
        100% - ${QR_CODE_HEIGHT_WHEN_HOVER}px - ${QR_CODE_LEFT_PADDING}px
      );
    }

    ${DownloadText} {
      opacity: 0;
      transform: translate(-90%, -90%);
    }

    ${ScanQRText} {
      transform: translate(0%, 0%);
      opacity: 1;
    }

    ${StyledBrandLogo} {
      opacity: 0;
      transform: translateY(-30%);
    }
  }
`;

const Wrapper = styled.div<{$justifyEnd?: boolean}>`
  align-items: center;
  background-color: ${COLORS.White};
  display: flex;
  justify-content: ${({$justifyEnd}) =>
    $justifyEnd ? 'end' : 'space-between'};
  padding-inline: 16px;
  height: ${FOOTER_HEIGHT}px;
  max-height: ${FOOTER_HEIGHT}px;
  flex: 1 0 ${FOOTER_HEIGHT}px;
`;

const StyledBadgeWithBorderRadius = styled(BadgeWithBorderRadius)<{
  $isDisabled?: boolean;
}>`
  font-size: ${FONT_SIZES.px10};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  span {
    line-height: ${FONT_SIZES.px16};
  }
  padding-block: 3px;

  ${({$isDisabled}) =>
    $isDisabled &&
    css`
      background-color: ${COLORS.Grey4};
      color: ${COLORS.Grey1};
    `}
`;

const StyledDownloadAppQr = styled(DownloadAppQr)`
  ${paddingCssVariable}: var(--hover-footer-padding);
  width: 100%;
  height: 100%;

  transition: transform ${ANIMATION_DURATION_TIME}ms ease-in-out;
`;
